import { InputRule, mergeAttributes, Node, PasteRule } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';

// inline-block keeps the browser from drawing the caret inside the pill's box at its edges.
const PILL_CLASSES =
    'inline-block rounded-full bg-mp-primary-content px-2 py-0.5 align-baseline font-mono text-[0.85em] leading-tight text-mp-link';

export const placeholderLabel = (token: string): string => token.slice(2, -2).trim();

const withFlags = (pattern: RegExp, flags: string) => new RegExp(pattern.source, flags);

type Direction = 'before' | 'after';

export const PlaceholderNode = Node.create<{ pattern: RegExp }>({
    name: 'placeholder',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: false,
    addOptions() {
        return { pattern: /\{\{[^{}]+\}\}/ };
    },
    addAttributes() {
        return {
            token: {
                default: '',
                parseHTML: (element) => element.getAttribute('data-placeholder') ?? '',
                renderHTML: (attributes) => ({ 'data-placeholder': attributes.token as string }),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'span[data-placeholder]' }];
    },
    renderHTML({ node, HTMLAttributes }) {
        return ['span', mergeAttributes(HTMLAttributes, { class: PILL_CLASSES }), placeholderLabel(node.attrs.token as string)];
    },
    renderText({ node }) {
        return node.attrs.token as string;
    },
    addInputRules() {
        return [
            new InputRule({
                find: new RegExp(`(?:${this.options.pattern.source})$`),
                handler: ({ state, range, match }) => {
                    state.tr.replaceWith(range.from, range.to, this.type.create({ token: match[0] }));
                },
            }),
        ];
    },
    addPasteRules() {
        return [
            new PasteRule({
                find: withFlags(this.options.pattern, 'g'),
                handler: ({ state, range, match }) => {
                    state.tr.replaceWith(range.from, range.to, this.type.create({ token: match[0] }));
                },
            }),
        ];
    },
    addKeyboardShortcuts() {
        const adjacentPill = (direction: Direction) => {
            const { selection } = this.editor.state;
            if (!selection.empty) return null;
            const node = direction === 'before' ? selection.$from.nodeBefore : selection.$from.nodeAfter;
            return node?.type.name === this.name ? node : null;
        };
        const deleteAdjacent = (direction: Direction) => {
            const pill = adjacentPill(direction);
            if (!pill) return false;
            const from = this.editor.state.selection.from - (direction === 'before' ? pill.nodeSize : 0);
            return this.editor.commands.deleteRange({ from, to: from + pill.nodeSize });
        };
        const stepOver = (direction: Direction) => {
            const pill = adjacentPill(direction);
            if (!pill) return false;
            const { state, view } = this.editor;
            const target = state.selection.from + (direction === 'before' ? -pill.nodeSize : pill.nodeSize);
            view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, target)));
            return true;
        };
        return {
            Backspace: () => deleteAdjacent('before'),
            Delete: () => deleteAdjacent('after'),
            ArrowLeft: () => stepOver('before'),
            ArrowRight: () => stepOver('after'),
        };
    },
});
